import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { liveClassesAPI } from '../services/api';
import useToast from '../hooks/useToast';
import { db } from '../firebase.js';
import { 
    collection, 
    addDoc, 
    onSnapshot, 
    query, 
    orderBy, 
    limit, 
    serverTimestamp, 
    doc, 
    setDoc, 
    deleteDoc 
} from 'firebase/firestore';
import auth from '../services/auth';
import { motion, AnimatePresence } from 'framer-motion';

const extractYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|live\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

const LiveClassRoom = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [videoUrl, setVideoUrl] = useState(null);
    const [rawUrl, setRawUrl] = useState(null);
    const [ytId, setYtId] = useState(null);
    const [classDetails, setClassDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [viewers, setViewers] = useState(0); 
    const [participants, setParticipants] = useState([]);
    const [activeReactions, setActiveReactions] = useState([]);
    const currentUser = auth.getCurrentUser() || { id: 'anon-' + Math.random().toString(36).substr(2, 9), name: 'Guest' };

    // --- REAL-TIME PRESENCE ---
    useEffect(() => {
        if (!id) return;
        const presenceRef = doc(db, `live_classes/${id}/presence/${currentUser.id}`);
        
        // Register current user
        setDoc(presenceRef, {
            name: currentUser.name || currentUser.full_name || 'Guest',
            joinedAt: serverTimestamp(),
            color: '#' + Math.floor(Math.random()*16777215).toString(16) // Random color for avatar
        });

        // Listen for all active participants
        const q = query(collection(db, `live_classes/${id}/presence`));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = [];
            snapshot.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
            setParticipants(list);
            setViewers(list.length);
        });

        // Cleanup: remove user from presence
        return () => {
            unsubscribe();
            deleteDoc(presenceRef);
        };
    }, [id, currentUser.id, currentUser.name, currentUser.full_name]);

    // --- REAL-TIME REACTIONS ---
    useEffect(() => {
        if (!id) return;
        const reactionsRef = collection(db, `live_classes/${id}/reactions`);
        const q = query(reactionsRef, orderBy('createdAt', 'desc'), limit(10));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    const data = change.doc.data();
                    // Avoid playing old reactions on initial load
                    if (data.createdAt && (Date.now() - data.createdAt.toMillis() < 5000)) {
                        triggerReaction(data.type);
                    }
                }
            });
        });

        return () => unsubscribe();
    }, [id]);

    const triggerReaction = (type) => {
        const rid = Date.now() + Math.random();
        setActiveReactions(prev => [...prev, { id: rid, type }]);
        setTimeout(() => {
            setActiveReactions(prev => prev.filter(r => r.id !== rid));
        }, 4000);
    };

    const sendReaction = async (type) => {
        try {
            await addDoc(collection(db, `live_classes/${id}/reactions`), {
                type,
                userId: currentUser.id,
                createdAt: serverTimestamp()
            });
        } catch (e) {
            console.error("Reaction failed:", e);
        }
    };


    useEffect(() => {
        const joinClass = async () => {
            try {
                const res = await liveClassesAPI.join(id);
                const url = res.data?.meeting_url;
                setClassDetails(res.data?.class_details || null);
                
                if (url) {
                    setRawUrl(url);
                    const extractedId = extractYouTubeId(url);
                    if (extractedId) {
                        setYtId(extractedId);
                        setVideoUrl(`https://www.youtube.com/embed/${extractedId}?autoplay=1`);
                    } else {
                        setVideoUrl(null);
                    }
                } else {
                    addToast('No streaming URL provided for this class.', 'error');
                }
            } catch (error) {
                console.error('Failed to join live class room', error);
                const msg = error.response?.data?.detail || 'Unable to join live class.';
                addToast(msg, 'error');
                navigate('/live-classes');
            } finally {
                setLoading(false);
            }
        };
        joinClass();
    }, [id, addToast, navigate]);

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.loadingContainer}>
                    <div style={styles.spinner}></div>
                    <p style={styles.loadingText}>Loading Premium Classroom...</p>
                </div>
            </div>
        );
    }

    const isLive = true; // FORCED LIVE FOR DEMO: classDetails?.status === 'LIVE_NOW';
    const isCompleted = classDetails?.status === 'COMPLETED';
    const chatUrl = ytId && isLive ? `https://www.youtube.com/live_chat?v=${ytId}&embed_domain=${window.location.hostname}` : null;

    return (
        <div style={styles.page}>
            {/* Premium Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <div style={styles.branding}>
                        <span style={styles.logoText}>ARIVUNEST</span>
                        <div style={styles.divider}></div>
                    </div>
                    <button style={styles.backBtn} onClick={() => navigate('/live-classes')}>
                        &larr; Back
                    </button>
                    <div>
                        <h1 style={styles.classTitle}>{classDetails?.title || 'Live Class Session'}</h1>
                        <p style={styles.classInstructor}>Instructor: {classDetails?.instructor_name || 'ARIVUNEST'} • {new Date(classDetails?.start_time).toLocaleDateString()}</p>
                    </div>
                </div>
                <div style={styles.headerRight}>
                    {isLive && <div style={styles.badgeLive}>🟢 LIVE</div>}
                    {isCompleted && <div style={styles.badgeCompleted}>✅ RECORDING</div>}
                    {!isLive && !isCompleted && <div style={styles.badgeScheduled}>🗓 WAITLIST</div>}
                </div>
            </div>

            {/* Content Area */}
            <div style={isLive ? styles.contentLive : styles.contentVOD}>
                {isLive ? (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Top Media Row: Video & Chat aligned */}
                        <div style={styles.mediaRow}>
                            <div style={styles.videoColumn}>
                                <div style={styles.videoWrapperLive}>
                                    {videoUrl ? (
                                        <>
                                            <iframe
                                                title="Live Class Video"
                                                style={styles.iframe}
                                                src={videoUrl}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                            <div style={styles.viewerCounter}>
                                                <span style={styles.pulseDot}></span>
                                                {viewers.toLocaleString()} watching now
                                            </div>
                                        </>
                                    ) : (
                                        <div style={styles.fallbackBox}>
                                            <h2>Stream Not Available</h2>
                                            {rawUrl && (
                                                <button style={styles.joinExternalBtn} onClick={() => window.open(rawUrl, '_blank')}>
                                                    Join External Stream
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {ytId && (
                                <div style={styles.chatColumn}>
                                    <div style={styles.chatWrapper}>
                                        <div style={styles.chatHeader}>Live Interaction</div>
                                        <iframe
                                            title="Live Chat"
                                            style={styles.chatIframe}
                                            src={chatUrl}
                                            frameBorder="0"
                                        ></iframe>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bottom Info Row: Toolbar, Tabs & Participants */}
                        <div style={styles.infoRow}>
                            <div style={styles.mainColumn}>
                                <div style={styles.toolbar}>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button style={styles.toolBtn} onClick={() => {
                                            addToast('Hand raised! Instructor notified.', 'success');
                                            sendReaction('✋');
                                        }}>✋ Raise Hand</button>
                                    </div>
                                    <div style={styles.reactions}>
                                        <button style={styles.reactBtn} onClick={() => sendReaction('❤️')}>❤️</button>
                                        <button style={styles.reactBtn} onClick={() => sendReaction('👏')}>👏</button>
                                        <button style={styles.reactBtn} onClick={() => sendReaction('🔥')}>🔥</button>
                                        <button style={styles.reactBtn} onClick={() => sendReaction('😮')}>😮</button>
                                    </div>
                                </div>

                                <div style={styles.detailsSection}>
                                    <div style={styles.tabList}>
                                        <button 
                                            style={{...styles.tabLink, ...(activeTab === 'overview' ? styles.tabActive : {})}} 
                                            onClick={() => setActiveTab('overview')}
                                        >Session Overview</button>
                                        <button 
                                            style={{...styles.tabLink, ...(activeTab === 'materials' ? styles.tabActive : {})}} 
                                            onClick={() => setActiveTab('materials')}
                                        >Class Materials</button>
                                        <button 
                                            style={{...styles.tabLink, ...(activeTab === 'notes' ? styles.tabActive : {})}} 
                                            onClick={() => setActiveTab('notes')}
                                        >Expert Notes</button>
                                    </div>
                                    <div style={styles.tabContent}>
                                        {activeTab === 'overview' && (
                                            <div style={styles.overviewPane}>
                                                <h3>About this session</h3>
                                                <p>{classDetails?.description || 'No description available for this Masterclass.'}</p>
                                                <div style={styles.instructorMini}>
                                                    <div style={styles.avatar}>A</div>
                                                    <div>
                                                        <strong>{classDetails?.instructor_name}</strong>
                                                        <p>Senior English Language Expert</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {activeTab === 'materials' && (
                                            <div style={styles.materialPane}>
                                                <div style={styles.materialCard}>
                                                    <span>📄 Advanced_Grammar_Cheat_Sheet.pdf</span>
                                                    <button style={styles.downloadBtn}>Download</button>
                                                </div>
                                                <div style={styles.materialCard}>
                                                    <span>📄 Vocab_Expansion_Session_04.pdf</span>
                                                    <button style={styles.downloadBtn}>Download</button>
                                                </div>
                                            </div>
                                        )}
                                        {activeTab === 'notes' && (
                                            <div style={styles.notesPane}>
                                                <p>• Practice the "Shadowing" technique for 15 mins daily.</p>
                                                <p>• Avoid literal translation from mother tongue.</p>
                                                <p>• Focus on intonation and rhythm in today's module.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div style={styles.participantsColumn}>
                                <div style={styles.participantList}>
                                    <div style={styles.sidebarSubHeader}>Recent Participants</div>
                                    {participants.map(p => (
                                        <div key={p.id} style={styles.participantItem}>
                                            <div style={{...styles.smallAvatar, background: p.color || '#4f46e5'}}>{p.name ? p.name[0].toUpperCase() : '?'}</div>
                                            <span style={styles.participantName}>{p.name} {p.id === currentUser.id ? '(You)' : ''}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={styles.mainColumn}>
                        <div style={styles.videoWrapperVOD}>
                            {videoUrl && (
                                <iframe
                                    title="Lesson Video"
                                    style={styles.iframe}
                                    src={videoUrl}
                                    allowFullScreen
                                ></iframe>
                            )}
                        </div>
                        <div style={styles.detailsSection}>
                            <div style={styles.tabContent}>
                                <h3>{classDetails?.title}</h3>
                                <p>{classDetails?.description}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Floating Reactions Overlay */}
                <div style={styles.reactionOverlay}>
                    <AnimatePresence>
                        {activeReactions.map(r => (
                            <motion.div
                                key={r.id}
                                initial={{ y: 0, x: Math.random() * 40 - 20, opacity: 1, scale: 0.5 }}
                                animate={{ y: -400, opacity: 0, scale: 2 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 3, ease: "easeOut" }}
                                style={styles.floatingEmoji}
                            >
                                {r.type}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

const styles = {
    page: { 
        minHeight: '100vh', 
        background: '#0B1121', 
        paddingTop: '0px',
        display: 'flex',
        flexDirection: 'column',
    },
    loadingContainer: { 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        flex: 1
    },
    spinner: {
        width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite'
    },
    loadingText: { color: '#94a3b8', fontSize: '18px', marginTop: '16px' },
    
    header: {
        padding: '12px 24px',
        marginTop: '0px',
        background: '#0f172a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #1e293b',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
    branding: { display: 'flex', alignItems: 'center', gap: '16px' },
    logoText: { color: '#3b82f6', fontWeight: '800', fontSize: '18px', letterSpacing: '1px' },
    divider: { width: '1px', height: '24px', background: '#334155' },
    backBtn: { background: 'rgba(255,255,255,0.05)', border: '1px solid #334155', color: '#94a3b8', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s' },
    classTitle: { margin: 0, fontSize: '18px', color: '#f8fafc', fontWeight: '700' },
    classInstructor: { margin: 0, fontSize: '12px', color: '#64748b', marginTop: '2px' },
    headerRight: { display: 'flex', alignItems: 'center' },
    badgeLive: { background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', animation: 'pulse 2s infinite' },
    badgeCompleted: { background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' },
    badgeScheduled: { background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.3)', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' },
    contentLive: { 
        flex: 1, 
        display: 'flex', 
        padding: '16px', 
        gap: '16px', 
        maxWidth: '100%', 
        margin: '0 auto', 
        width: '100%', 
        boxSizing: 'border-box',
        alignItems: 'stretch', // Ensure children (columns) have equal height
    },
    contentVOD: { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
    mainColumn: { 
        flex: '1', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px', 
        minWidth: 0 
    },
    mediaRow: {
        display: 'flex',
        gap: '16px',
        width: '100%',
        alignItems: 'stretch',
    },
    videoColumn: {
        flex: 1,
        minWidth: 0,
    },
    chatColumn: {
        width: '320px',
        flexShrink: 0,
    },
    infoRow: {
        display: 'flex',
        gap: '16px',
        width: '100%',
    },
    participantsColumn: {
        width: '320px',
        flexShrink: 0,
    },
    videoWrapperLive: { 
        width: '100%', 
        background: '#000', 
        borderRadius: '16px', 
        overflow: 'hidden', 
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', 
        aspectRatio: '16/9', 
        position: 'relative',
        height: '100%',
    },
    videoWrapperVOD: { width: '100%', aspectRatio: '16/9', background: '#000', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' },
    iframe: { width: '100%', height: '100%', border: 'none' },
    viewerCounter: { position: 'absolute', top: '16px', left: '16px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', color: '#fff', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', zIndex: 10 },
    pulseDot: { width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)', animation: 'pulse-dot 1.5s infinite' },
    toolbar: { background: '#1e293b', padding: '12px 24px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #334155', marginBottom: '16px' },
    toolBtn: { background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
    reactions: { display: 'flex', gap: '12px' },
    reactBtn: { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', filter: 'grayscale(0.3)', transition: 'transform 0.2s' },
    detailsSection: { background: '#1e293b', borderRadius: '16px', overflow: 'hidden', border: '1px solid #334155', flex: 1 },
    tabList: { display: 'flex', background: '#0f172a', borderBottom: '1px solid #334155' },
    tabLink: { padding: '16px 24px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s', borderBottom: '2px solid transparent' },
    tabActive: { color: '#3b82f6', borderBottom: '2px solid #3b82f6', background: 'rgba(59, 130, 246, 0.05)' },
    tabContent: { padding: '24px', color: '#e2e8f0', minHeight: '200px' },
    overviewPane: { display: 'flex', flexDirection: 'column', gap: '16px' },
    instructorMini: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px' },
    avatar: { width: '40px', height: '40px', background: '#3b82f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' },
    materialPane: { display: 'flex', flexDirection: 'column', gap: '12px' },
    materialCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' },
    downloadBtn: { background: 'none', border: '1px solid #3b82f6', color: '#3b82f6', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
    notesPane: { lineHeight: '2', color: '#94a3b8' },
    sidebarRight: { 
        flex: '0 0 320px', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px',
    },
    chatWrapper: { 
        height: '100%', 
        background: '#1e293b', 
        borderRadius: '12px', 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column', 
        border: '1px solid #334155',
    },
    chatHeader: { padding: '10px', background: '#0f172a', color: '#f8fafc', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid #334155', textAlign: 'center' },
    chatIframe: { flex: 1, width: '100%', border: 'none', background: '#fff' },
    participantList: { 
        background: '#1e293b', 
        borderRadius: '12px', 
        padding: '20px', 
        border: '1px solid #334155',
        height: '100%',
    },
    sidebarSubHeader: { fontSize: '14px', fontWeight: '700', color: '#f8fafc', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' },
    participantItem: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
    smallAvatar: { width: '28px', height: '28px', borderRadius: '50%', color: '#fff', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' },
    participantName: { fontSize: '13px', color: '#94a3b8' },
    fallbackBox: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#f8fafc', padding: '40px', textAlign: 'center' },
    joinExternalBtn: { marginTop: '20px', background: '#3b82f6', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' },
    reactionOverlay: { position: 'absolute', bottom: '150px', left: '100px', pointerEvents: 'none', zIndex: 100, width: '200px', height: '500px', display: 'flex', justifyContent: 'center' },
    floatingEmoji: { position: 'absolute', fontSize: '40px', filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.5))' }
};

const animationStyles = `
@keyframes pulse-dot {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.5); opacity: 0.5; }
    100% { transform: scale(1); opacity: 1; }
}
@keyframes spin {
    to { transform: rotate(360deg); }
}
@keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
}
`;

if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = animationStyles;
    document.head.appendChild(styleSheet);
}

export default LiveClassRoom;
